/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { Button, Modal, Tab, TabList, TabPanel, Tabs } from '@ecsper/ui-primitives';
import { useEffect, useId, useState } from 'react';

import { formatRelativeTime } from './format-relative-time.js';

/** Office option presented to the user. Matches CTR-PL-MS-OFFICESSO-001 SS 6.1. */
export interface OfficeOption {
  officeId: string;
  officeName: string;
  organisationName: string;
  /** Display role (for example, 'Member', 'Owner'). */
  role: string;
  /** Optional last-used timestamp (ISO) to help user choose. */
  lastUsedAt?: string;
}

export interface OfficePickerProps {
  /** Whether the picker is open. */
  open: boolean;
  /** Office options to choose from. */
  offices: readonly OfficeOption[];
  /** Called when user confirms a selection. */
  onSelect: (officeId: string) => void;
  /** Called when user cancels (Modal close or Cancel button). */
  onCancel: () => void;
}

/**
 * Lets the user resolve AMBIGUOUS_OFFICE SSO by picking an office (OI-7).
 * Offices are presented with {@link Tabs}: tab labels name each office; panels show detail.
 */
export function OfficePicker({ open, offices, onSelect, onCancel }: OfficePickerProps) {
  const summaryId = useId();
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('');

  useEffect(() => {
    if (!open) {
      return;
    }
    const first = offices[0]?.officeId;
    setSelectedOfficeId(first !== undefined ? first : '');
  }, [open, offices]);

  const selectedOffice = offices.find((o) => o.officeId === selectedOfficeId);

  const emptyState = offices.length === 0;

  return (
    <Modal open={open} onClose={onCancel} title="Choose an office">
      {emptyState ? (
        <p className="ecs-office-picker__empty">
          No offices available. Contact your administrator.
        </p>
      ) : (
        <>
          <p className="ecs-office-picker__intro">
            Select the office you want to use for this session, then choose Continue.
          </p>
          <div id={summaryId} className="ecs-office-picker__sr-summary">
            {selectedOffice !== undefined
              ? `Selected office ${selectedOffice.officeName}, organisation ${selectedOffice.organisationName}, role ${selectedOffice.role}.`
              : ''}
          </div>
          <div
            className="ecs-office-picker__tabs-wrap"
            onKeyDownCapture={(e) => {
              if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
                return;
              }
              const active = document.activeElement as HTMLElement | null;
              if (active === null || active.closest('.ecs-office-picker__tab-list') === null) {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              const ids = offices.map((o) => o.officeId);
              const index = ids.indexOf(selectedOfficeId);
              if (index < 0 || ids.length === 0) {
                return;
              }
              const nextIndex =
                e.key === 'ArrowDown'
                  ? Math.min(index + 1, ids.length - 1)
                  : Math.max(index - 1, 0);
              const nextId = ids[nextIndex];
              if (nextId === undefined) {
                return;
              }
              setSelectedOfficeId(nextId);
              queueMicrotask(() => {
                document
                  .querySelector<HTMLButtonElement>(`[data-tab-value="${CSS.escape(nextId)}"]`)
                  ?.focus();
              });
            }}
          >
            <Tabs value={selectedOfficeId} onValueChange={setSelectedOfficeId}>
              <TabList className="ecs-office-picker__tab-list">
                {offices.map((office) => (
                  <Tab
                    key={office.officeId}
                    value={office.officeId}
                    className="ecs-office-picker__tab"
                    aria-describedby={summaryId}
                  >
                    {office.officeName}
                  </Tab>
                ))}
              </TabList>
              {offices.map((office) => {
                const lastUsed =
                  office.lastUsedAt !== undefined && office.lastUsedAt !== ''
                    ? formatRelativeTime(office.lastUsedAt)
                    : undefined;
                return (
                  <TabPanel key={office.officeId} value={office.officeId}>
                    <div className="ecs-office-picker__option-body">
                      <span className="ecs-office-picker__org-line">{office.organisationName}</span>
                      <span className="ecs-office-picker__role-line">Role: {office.role}</span>
                      {lastUsed !== undefined ? (
                        <span className="ecs-office-picker__last-used">Last used {lastUsed}</span>
                      ) : null}
                    </div>
                  </TabPanel>
                );
              })}
            </Tabs>
          </div>
          <div className="ecs-office-picker__actions">
            <Button type="button" variant="secondary" size="md" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={selectedOfficeId === ''}
              onClick={() => {
                if (selectedOfficeId !== '') {
                  onSelect(selectedOfficeId);
                }
              }}
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
